
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ModalPosition, ModalProps } from '../../types/ModalTypes';


const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'w-full',
    half: 'w-1/2',
    two_third: 'w-2/3',
    three_fourth: 'w-3/4',
    two_five: 'w-2/5'
} as const;

const heightClasses = {
    auto: 'h-auto',
    full: 'h-full',
    half: 'h-1/2',
    three_fourth: 'h-3/4',
} as const;

const positionStyles: Record<ModalPosition, { initial: string; active: string }> = {
    center: {
        initial: 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-95 opacity-0',
        active: 'scale-100 opacity-100'
    },
    right: {
        initial: 'fixed right-0 top-0 h-full transform translate-x-full',
        active: '!translate-x-0'
    },
    left: {
        initial: 'fixed left-0 top-0 h-full transform -translate-x-full',
        active: '!translate-x-0'
    },
    top: {
        initial: 'fixed top-0 left-1/2 transform -translate-x-1/2 -translate-y-full',
        active: '!translate-y-0'
    },
    bottom: {
        initial: 'fixed bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full',
        active: '!translate-y-0'
    }
};

const getPositionStyles = (position: ModalPosition, isAnimating: boolean): string => {
    const styles = positionStyles[position];
    return `${styles.initial} ${isAnimating ? styles.active : ''}`;
};

export default function Modal({
    isOpen,
    onClose,
    children,
    size = 'md',
    height = 'auto',
    position = 'center',
    title,
    className = '',
    customHeight,
    customWidth,
    showBackdrop = true,
}: ModalProps) {
    const [shouldRender, setShouldRender] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen && !shouldRender) {
            setShouldRender(true);
            // Delay the animation start
            setTimeout(() => {
                setIsAnimating(true);
            }, 50);
        } else if (!isOpen && shouldRender) {
            setIsAnimating(false);
            // Wait for animation to finish before unmounting
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    // Prevent body scroll and interactions when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('modal-open');
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('modal-open');
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    if (!shouldRender) return null;

    const sizeClass = customWidth || sizeClasses[size];
    const heightClass = customHeight || heightClasses[height];
    const positionClass = getPositionStyles(position, isAnimating);

    return (
        <div 
            className="fixed inset-0 z-[9999]" 
            style={{ pointerEvents: 'auto' }}
        >
            {/* Backdrop */}
            {showBackdrop && (
                <div
                    onClick={onClose}
                    className={`
            fixed inset-0 bg-black/70 transition-opacity duration-300 cursor-pointer
            ${isAnimating ? 'opacity-100' : 'opacity-0'}
          `}
                    style={{ pointerEvents: 'auto' }}
                />
            )}

            {/* Modal */}
            <div
                className={`
          bg-white dark:bg-gray-900 shadow-2xl backdrop-blur-sm
          transition-all duration-300 ease-out
          flex flex-col relative z-10
          ${sizeClass}
          ${heightClass}
          ${positionClass}
          ${className}
        `}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ pointerEvents: 'auto' }}
            >
                {/* Header */}
                {title && (
                    <div className="flex items-center justify-between p-6 pb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {title}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {children}
                </div>
            </div>
        </div>
    );
}